/**
 * CopilotHomePage — Workbench-style landing for the Copilot agent.
 *
 * Layout (md+):
 *   ┌──────────────┬────────────────────────────────┐
 *   │ Session rail │  Main: greeting + ask bar +    │
 *   │ (collapsible)│        suggestion chips        │
 *   │ ──────────── │                                │
 *   │  Search      │                                │
 *   │  ▢ session 1 │                                │
 *   │  ▢ session 2 │                                │
 *   │  …           │                                │
 *   │  + New       │                                │
 *   └──────────────┴────────────────────────────────┘
 *
 * Why a rail?
 *   • Power tenants have 200+ sessions; a flat in-page list doesn't scale.
 *   • ChatGPT/Claude/Cursor pattern — zero learning curve.
 *   • Lets the main ask zone stay deliberately narrow (~720px) so the
 *     textarea reads as a focused affordance instead of a stretched form.
 *
 * Mobile (< md): the rail collapses into an inline session list rendered
 * BELOW the main ask zone. Single column, page scrolls naturally — no
 * fancy drawers, no double scroll surfaces.
 *
 * The rail's collapsed state persists in localStorage so power users get
 * their preferred layout on every visit.
 *
 * Tier-0 implementation: uses only data already exposed by the existing
 * threads endpoint (thread_id, title, created_at). Status dots, last-finding
 * previews, approval inbox, and pinning are intentionally deferred until the
 * matching backend signals exist.
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Skeleton,
  useTheme,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import ClearRounded from '@mui/icons-material/ClearRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import KeyboardDoubleArrowLeftRounded from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import KeyboardDoubleArrowRightRounded from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import { buildApiUrl, tenantApiPrefix } from '../../config';
import { getTenantIdFromStorage } from '../../utils/onboardingLocalStorage';
import { formatRelativeTime } from '../../utils/date';
import type { CopilotThread } from './stores/useCopilotStore';
import { useCopilotStore } from './stores/useCopilotStore';
import { useProductOnboarding } from '../../hooks/useProductOnboarding';
import OnboardingCoachmark from '../../components/OnboardingCoachmark';
import { ConfirmDialog } from '../../components/dialog';
import { ONBOARDING_DATA_OVERVIEW_PROMPT } from '../../constants/copilotOnboarding';
import {
  trackOnboardingCopilotPrefillSent,
  trackOnboardingCopilotStarterChipClicked,
} from '../../utils/analytics';

// ── Suggested prompts (fallback when starter_questions API is unavailable) ──
const DEFAULT_SUGGESTED_PROMPTS = [
  'What was total revenue last month?',
  'Why did order volume drop in Q4?',
  'Show me top 10 customers by spend',
  'Compare this month vs same month last year',
];

// ── Helpers ────────────────────────────────────────────────────────────────
function getTimeOfDayGreeting(now: Date = new Date()): string {
  const h = now.getHours();
  if (h < 5) return 'Up late';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function getFirstName(): string | null {
  try {
    const raw = window.localStorage.getItem('klaris_user');
    if (!raw) return null;
    const u = JSON.parse(raw);
    const full = (u?.full_name || u?.name || '').trim();
    if (full) return full.split(/\s+/)[0] || null;
    const email = (u?.email || '').trim();
    if (email) {
      const local = email.split('@')[0] || '';
      const cleaned = local.split(/[._+-]/)[0] || '';
      if (cleaned) return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    }
  } catch { /* ignore */ }
  return null;
}

function truncate(s: string, n: number): string {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s;
}

/** Number of total sessions at which we switch to power-user chip view. */
const POWER_USER_THRESHOLD = 5;
/** Suggestion chips a power user sees by default alongside the Continue chip. */
const POWER_USER_CHIP_LIMIT = 3;
/** Max characters per suggestion chip label. */
const CHIP_LABEL_MAX = 60;
/** Rail width (expanded / collapsed). 288 fits long titles cleanly; 52 is
 *  a comfortable icon strip — Linear-/Cursor-style. */
const RAIL_WIDTH_EXPANDED = 288;
const RAIL_WIDTH_COLLAPSED = 52;
/** localStorage key for collapsed-rail preference. */
const RAIL_COLLAPSED_KEY = 'klaris.copilot.home.railCollapsed';
/** Main content max-width — narrow enough that the input feels focused. */
const MAIN_MAX_WIDTH = 720;

// ── Rail session row ───────────────────────────────────────────────────────
// Compact, stacked variant of the session row optimized for the narrow rail.
// Title sits on top; relative time underneath in a smaller muted style — same
// pattern as Slack channel rows and Linear's issue list, where width is
// scarce and titles dominate.
interface RailSessionItemProps {
  thread: CopilotThread;
  onOpen: (threadId: string) => void;
  onRequestDelete: (thread: CopilotThread) => void;
}

function RailSessionItem({ thread, onOpen, onRequestDelete }: RailSessionItemProps) {
  const title = thread.title || 'Untitled session';
  const timeText = thread.createdAt ? formatRelativeTime(thread.createdAt) : '';
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onOpen(thread.threadId)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(thread.threadId);
        }
      }}
      title={title}
      sx={(t) => ({
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 1.25,
        py: 1,
        cursor: 'pointer',
        borderRadius: `${t.ds.radius.sm}px`,
        transition: `background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
        '&:hover': { bgcolor: t.tokens.surface.sunken },
        '&:focus-visible': {
          outline: `2px solid ${t.tokens.brand.primary}`,
          outlineOffset: -2,
          bgcolor: t.tokens.surface.sunken,
        },
        // Reveal the delete affordance on hover/focus-within. Linear/ChatGPT
        // pattern — keeps the row visually quiet until intent is shown.
        '&:hover .session-row-actions, &:focus-within .session-row-actions': {
          opacity: 1,
          pointerEvents: 'auto',
        },
      })}
    >
      <ChatBubbleOutlineRounded
        sx={(t) => ({
          fontSize: 14,
          color: t.tokens.text.tertiary,
          flexShrink: 0,
          mt: '2px',
        })}
      />
      <Box sx={{ minWidth: 0, flex: 1, pr: 3 }}>
        <Typography
          noWrap
          sx={(t) => ({
            fontWeight: 500,
            fontSize: '0.8125rem',
            lineHeight: 1.35,
            color: t.tokens.text.primary,
          })}
        >
          {title}
        </Typography>
        {timeText && (
          <Typography
            sx={(t) => ({
              mt: 0.125,
              fontSize: '0.6875rem',
              lineHeight: 1.3,
              color: t.tokens.text.tertiary,
            })}
          >
            {timeText}
          </Typography>
        )}
      </Box>
      <Box
        className="session-row-actions"
        sx={{
          position: 'absolute',
          top: '50%',
          right: 4,
          transform: 'translateY(-50%)',
          opacity: 0,
          pointerEvents: 'none',
          transition: 'opacity 120ms ease',
        }}
      >
        <Tooltip title="Delete session" placement="top">
          <IconButton
            size="small"
            aria-label={`Delete session: ${title}`}
            onClick={(e) => {
              e.stopPropagation();
              onRequestDelete(thread);
            }}
            onKeyDown={(e) => {
              // Don't let Enter/Space bubble up to the row's "open" handler.
              if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
            }}
            sx={(t) => ({
              width: 24,
              height: 24,
              color: t.tokens.text.tertiary,
              borderRadius: `${t.ds.radius.sm}px`,
              '&:hover': {
                color: t.palette.error.main,
                bgcolor: t.palette.mode === 'light'
                  ? 'rgba(220, 38, 38, 0.08)'
                  : 'rgba(248, 113, 113, 0.12)',
              },
            })}
          >
            <DeleteOutlineRounded sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function CopilotHomePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentStepId, shouldShow } = useProductOnboarding();

  const [threads, setThreads] = useState<CopilotThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [suggestedPrompts, setSuggestedPrompts] = useState<string[]>(DEFAULT_SUGGESTED_PROMPTS);
  /** True when starter_questions came from product-onboarding API (not hardcoded defaults). */
  const [starterQuestionsFromApi, setStarterQuestionsFromApi] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState<boolean>(() => {
    try { return window.localStorage.getItem(RAIL_COLLAPSED_KEY) === '1'; }
    catch { return false; }
  });
  const [pendingDelete, setPendingDelete] = useState<CopilotThread | null>(null);
  const [deleting, setDeleting] = useState(false);

  const sendBtnRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const firstName = useMemo(() => getFirstName(), []);
  const greeting = useMemo(() => getTimeOfDayGreeting(), []);

  // ── Persist rail collapse preference ─────────────────────────────────────
  useEffect(() => {
    try { window.localStorage.setItem(RAIL_COLLAPSED_KEY, railCollapsed ? '1' : '0'); }
    catch { /* ignore */ }
  }, [railCollapsed]);

  // ── Data fetching ─────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchThreads = async () => {
      try {
        const token = localStorage.getItem('klaris_jwt');
        const tid = getTenantIdFromStorage();
        if (!token || !tid) { setLoading(false); return; }

        const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/chat/threads`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) { setLoading(false); return; }

        const data = await resp.json();
        const copilotThreads: CopilotThread[] = (data.threads || [])
          .filter((t: any) => t.agent_type === 'copilot')
          .map((t: any) => ({
            threadId: t.thread_id,
            title: t.title,
            agentType: t.agent_type || 'copilot',
            createdAt: t.created_at || null,
          }));

        setThreads(copilotThreads);
      } catch (e) {
        console.error('[CopilotHomePage] Error fetching threads:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchThreads();
  }, []);

  // Prefill from dashboard "Add widget" (and similar) — user presses Enter on Copilot home to send.
  useEffect(() => {
    const state = location.state as { initialQuestion?: string } | undefined;
    const q = state?.initialQuestion?.trim();
    if (!q) return;
    setInputValue(q);
    navigate('.', { replace: true, state: {} });
  }, [location.key, navigate]);

  // Load onboarding starter questions (chips) and prefill the overview prompt during active product onboarding.
  useEffect(() => {
    const fetchStarterQuestions = async () => {
      try {
        const token = localStorage.getItem('klaris_jwt');
        const tenantRaw = localStorage.getItem('klaris_tenant');
        if (!token || !tenantRaw) return;
        let tenantId: string | null = null;
        try { tenantId = JSON.parse(tenantRaw).tenant_id; } catch { tenantId = tenantRaw; }
        if (!tenantId) return;

        const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/product-onboarding`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) return;
        const data = await resp.json();
        const qs: string[] = Array.isArray(data?.starter_questions) ? data.starter_questions : [];
        if (qs.length > 0) {
          setSuggestedPrompts(qs);
          setStarterQuestionsFromApi(true);
        } else {
          setStarterQuestionsFromApi(false);
        }

        if (shouldShow) {
          setInputValue((prev: string) => {
            if (prev.trim()) return prev;
            return ONBOARDING_DATA_OVERVIEW_PROMPT;
          });
        }
      } catch {
        // Non-critical — fallback prompts remain.
      }
    };
    fetchStarterQuestions();
  }, [shouldShow]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const len = el.value.length;
      try { el.setSelectionRange(len, len); } catch { /* ignore */ }
    });
  }, []);

  const handleNewSession = useCallback(() => {
    const store = useCopilotStore.getState();
    store.resetSession();
    const newId = crypto.randomUUID();
    store.setThreadId(newId);
    navigate(`/copilot/${newId}`);
  }, [navigate]);

  const startWithPromptAutoSend = useCallback(
    (prompt: string) => {
      const store = useCopilotStore.getState();
      store.resetSession();
      const newId = crypto.randomUUID();
      store.setThreadId(newId);
      store.setInputText(prompt);
      store.setPendingAutoSend(true);
      navigate(`/copilot/${newId}`);
    },
    [navigate],
  );

  const handlePrefillFromChip = useCallback(
    (prompt: string, chipIndex: number) => {
      trackOnboardingCopilotStarterChipClicked({
        question_index: chipIndex,
        uses_starter_questions: starterQuestionsFromApi,
      });
      setInputValue(prompt);
      focusInput();
    },
    [focusInput, starterQuestionsFromApi],
  );

  const handleInputSubmit = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (shouldShow && trimmed === ONBOARDING_DATA_OVERVIEW_PROMPT.trim()) {
      trackOnboardingCopilotPrefillSent();
    }
    startWithPromptAutoSend(trimmed);
  }, [inputValue, startWithPromptAutoSend, shouldShow]);

  const handleOpenThread = useCallback((threadId: string) => {
    const store = useCopilotStore.getState();
    store.resetSession();
    store.setThreadId(threadId);
    navigate(`/copilot/${threadId}`);
  }, [navigate]);

  const handleRequestDelete = useCallback((thread: CopilotThread) => {
    setPendingDelete(thread);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setDeleting(true);
    try {
      const token = localStorage.getItem('klaris_jwt');
      const tid = getTenantIdFromStorage();
      if (!token || !tid) throw new Error('Missing auth context');

      const resp = await fetch(
        buildApiUrl(`${tenantApiPrefix(tid)}/chat/threads/${target.threadId}`),
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!resp.ok && resp.status !== 404) {
        // 404 is fine — already gone, treat as success and remove locally.
        throw new Error(`Failed to delete thread (${resp.status})`);
      }
      setThreads((prev: CopilotThread[]) =>
        prev.filter((t: CopilotThread) => t.threadId !== target.threadId),
      );
      setPendingDelete(null);
    } catch (e) {
      console.error('[CopilotHomePage] Failed to delete thread:', e);
      // Leave the dialog open so the user can retry; surfacing a toast here
      // is the next iteration once we have a global toast service.
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const tokens = theme.tokens;
  const cardBg = tokens.surface.raised;
  const cardBorder = tokens.border.subtle;

  const lastThread = threads[0];
  const isPowerUser = threads.length >= POWER_USER_THRESHOLD;
  const isSearching = searchQuery.trim().length > 0;

  const filteredThreads = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) => (t.title || '').toLowerCase().includes(q));
  }, [threads, searchQuery]);

  /** Visible suggestion chips, truncated and (for power users) limited until expanded. */
  const visibleSuggestions = useMemo(() => {
    const items = suggestedPrompts.map((p: string, i: number) => ({ original: p, label: truncate(p, CHIP_LABEL_MAX), index: i }));
    if (!isPowerUser || showAllSuggestions) return items;
    return items.slice(0, POWER_USER_CHIP_LIMIT);
  }, [suggestedPrompts, isPowerUser, showAllSuggestions]);

  const stateStripText = useMemo(() => {
    if (loading) return '';
    if (threads.length === 0) {
      return 'Welcome — ask your first question to begin an investigation.';
    }
    const count = `${threads.length} session${threads.length === 1 ? '' : 's'}`;
    const last = lastThread?.createdAt ? formatRelativeTime(lastThread.createdAt) : '';
    return last ? `${count} · last active ${last}` : count;
  }, [loading, threads.length, lastThread]);

  // ── Sub-renders ──────────────────────────────────────────────────────────
  const renderRailList = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, px: 0.5 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={42} sx={{ borderRadius: `${theme.ds.radius.sm}px` }} />
          ))}
        </Box>
      );
    }
    if (threads.length === 0) {
      return (
        <Box sx={{ px: 1.5, py: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', color: tokens.text.tertiary, lineHeight: 1.5 }}>
            No sessions yet. Ask a question to begin.
          </Typography>
        </Box>
      );
    }
    if (filteredThreads.length === 0) {
      return (
        <Box sx={{ px: 1.5, py: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', color: tokens.text.tertiary, lineHeight: 1.5 }}>
            No matches for &ldquo;{searchQuery}&rdquo;.
          </Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, px: 0.5 }}>
        {filteredThreads.map((thread) => (
          <RailSessionItem
            key={thread.threadId}
            thread={thread}
            onOpen={handleOpenThread}
            onRequestDelete={handleRequestDelete}
          />
        ))}
      </Box>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 3, md: 2.5 },
        minHeight: 0,
      }}
    >
      {/* ─── Session Rail (md+) ─────────────────────────────────────────── */}
      <Box
        sx={(t) => ({
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          flexShrink: 0,
          width: railCollapsed ? RAIL_WIDTH_COLLAPSED : RAIL_WIDTH_EXPANDED,
          minHeight: 0,
          border: `1px solid ${t.tokens.border.subtle}`,
          borderRadius: `${t.ds.radius.lg}px`,
          bgcolor: t.tokens.surface.raised,
          overflow: 'hidden',
          transition: `width ${t.ds.motion.duration.normal}ms ${t.ds.motion.easing.standard}`,
        })}
      >
        {/* Rail header */}
        <Box
          sx={(t) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: railCollapsed ? 'center' : 'space-between',
            gap: 0.5,
            px: railCollapsed ? 0 : 1.5,
            py: 1,
            borderBottom: `1px solid ${t.tokens.border.subtle}`,
            minHeight: 44,
          })}
        >
          {!railCollapsed && (
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '0.8125rem',
                color: tokens.text.primary,
                letterSpacing: '-0.005em',
              }}
            >
              Sessions
              {threads.length > 0 && (
                <Box
                  component="span"
                  sx={{ ml: 0.75, fontWeight: 500, fontSize: '0.6875rem', color: tokens.text.tertiary }}
                >
                  {threads.length}
                </Box>
              )}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 0.25 }}>
            {!railCollapsed && (
              <Tooltip title="New session">
                <IconButton
                  size="small"
                  onClick={handleNewSession}
                  aria-label="New session"
                  sx={(t) => ({
                    width: 28,
                    height: 28,
                    color: t.tokens.text.tertiary,
                    '&:hover': {
                      color: t.tokens.brand.primary,
                      bgcolor: t.tokens.brand.primarySoft,
                    },
                  })}
                >
                  <AddRounded sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={railCollapsed ? 'Expand sessions' : 'Collapse sessions'}>
              <IconButton
                size="small"
                onClick={() => setRailCollapsed((v: boolean) => !v)}
                aria-label={railCollapsed ? 'Expand sessions' : 'Collapse sessions'}
                sx={(t) => ({
                  width: 28,
                  height: 28,
                  color: t.tokens.text.tertiary,
                  '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
                })}
              >
                {railCollapsed ? (
                  <KeyboardDoubleArrowRightRounded sx={{ fontSize: 16 }} />
                ) : (
                  <KeyboardDoubleArrowLeftRounded sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Rail body — collapsed shows just an icon strip; expanded shows search + list */}
        {railCollapsed ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              py: 1,
            }}
          >
            <Tooltip title="New session" placement="right">
              <IconButton
                size="small"
                onClick={handleNewSession}
                aria-label="New session"
                sx={(t) => ({
                  width: 32,
                  height: 32,
                  color: t.tokens.text.tertiary,
                  '&:hover': {
                    color: t.tokens.brand.primary,
                    bgcolor: t.tokens.brand.primarySoft,
                  },
                })}
              >
                <AddRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={`${threads.length} session${threads.length === 1 ? '' : 's'}`} placement="right">
              <IconButton
                size="small"
                onClick={() => setRailCollapsed(false)}
                aria-label="Show sessions"
                sx={(t) => ({
                  width: 32,
                  height: 32,
                  color: t.tokens.text.tertiary,
                  '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
                })}
              >
                <ChatBubbleOutlineRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <>
            {/* Search */}
            <Box sx={{ px: 1, py: 1, borderBottom: `1px solid ${cardBorder}` }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search sessions"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={threads.length === 0}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded sx={{ fontSize: 14, color: tokens.text.tertiary }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery('')}
                        aria-label="Clear search"
                        sx={{ p: 0.25 }}
                      >
                        <ClearRounded sx={{ fontSize: 13, color: tokens.text.tertiary }} />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                  sx: {
                    fontSize: '0.8125rem',
                    height: 30,
                    borderRadius: `${theme.ds.radius.sm}px`,
                    bgcolor: tokens.surface.sunken,
                    '& fieldset': { border: 'none' },
                    '&.Mui-focused': {
                      bgcolor: tokens.surface.raised,
                      boxShadow: `inset 0 0 0 1px ${tokens.brand.primary}`,
                    },
                  },
                }}
              />
            </Box>

            {/* Scrollable list */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 0.75 }}>
              {renderRailList()}
            </Box>
          </>
        )}
      </Box>

      {/* ─── Main column ────────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: { md: 1 },
          minWidth: 0,
          minHeight: 0,
          overflowY: { md: 'auto' },
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: MAIN_MAX_WIDTH, py: { md: 0.5 } }}>
          {/* Greeting + state strip */}
          <Box sx={{ mb: { xs: 3, md: 4 } }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.5rem', md: '1.75rem' },
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
                color: tokens.text.primary,
              }}
            >
              {firstName ? `${greeting}, ${firstName}.` : `${greeting}.`}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: '0.8125rem',
                color: tokens.text.tertiary,
                minHeight: '1.2em',
              }}
            >
              {stateStripText}
            </Typography>
          </Box>

          {/* Ask bar */}
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={10}
            placeholder="Ask Copilot…  e.g. why did revenue drop in March?"
            value={inputValue}
            autoFocus
            inputRef={inputRef}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleInputSubmit();
              }
            }}
            sx={{ mb: 1.5 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ alignSelf: 'flex-end', mb: 0.5, mr: 0.25 }}>
                  <span>
                    <Button
                      ref={sendBtnRef}
                      variant="contained"
                      disabled={!inputValue.trim()}
                      onClick={handleInputSubmit}
                      disableElevation
                      aria-label="Send"
                      sx={{
                        minWidth: 32,
                        width: 32,
                        height: 32,
                        p: 0,
                        borderRadius: `${theme.ds.radius.sm}px`,
                        textTransform: 'none',
                      }}
                    >
                      <SendRounded sx={{ fontSize: 16 }} />
                    </Button>
                  </span>
                </InputAdornment>
              ),
              sx: {
                borderRadius: `${theme.ds.radius.md}px`,
                bgcolor: cardBg,
                border: `1px solid ${tokens.border.default}`,
                fontSize: '0.9375rem',
                lineHeight: 1.5,
                py: 1.25,
                pr: 0.75,
                alignItems: 'flex-start',
                boxShadow: tokens.shadow.xs,
                transition: `border-color ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}, box-shadow ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}`,
                '& fieldset': { border: 'none' },
                '&:hover': { borderColor: tokens.border.strong },
                '&.Mui-focused': {
                  borderColor: tokens.brand.primary,
                  boxShadow: tokens.shadow.focusRing,
                },
                '& textarea': {
                  overflowY: 'auto !important',
                },
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: -0.25,
              mb: 2.25,
              fontSize: '0.6875rem',
              letterSpacing: '0.02em',
              color: tokens.text.tertiary,
            }}
          >
            Shift+Enter for a new line · Enter to send
          </Typography>
          <OnboardingCoachmark
            open={
              Boolean(shouldShow && currentStepId === 'first_question_asked' && inputValue.trim())
            }
            anchorRef={sendBtnRef}
            message="Run this first insight by clicking Send."
            placement="top-end"
          />

          {/* Prompt rail (Continue + suggestions) */}
          <Box sx={{ mb: { xs: 4, md: 2 } }}>
            <Typography
              sx={(t) => ({
                ...t.typography.eyebrow,
                color: t.tokens.text.tertiary,
                display: 'block',
                mb: 1,
              })}
            >
              Try asking
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.875 }}>
              {!loading && lastThread && (
                <Chip
                  key="__continue"
                  icon={<HistoryRounded sx={{ fontSize: '15px !important', ml: '4px !important' }} />}
                  label={`Continue: ${truncate(lastThread.title || 'Untitled session', 36)}`}
                  onClick={() => handleOpenThread(lastThread.threadId)}
                  sx={(t) => ({
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    height: 32,
                    px: 0.75,
                    bgcolor: t.tokens.brand.primarySoft,
                    border: `1px solid ${t.tokens.brand.primary}`,
                    color: t.tokens.brand.primary,
                    cursor: 'pointer',
                    borderRadius: `${t.ds.radius.full}px`,
                    transition: `background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}, opacity ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                    '& .MuiChip-icon': { color: t.tokens.brand.primary },
                    '&:hover': { opacity: 0.85 },
                  })}
                />
              )}
              {visibleSuggestions.map(({ original, label, index }: { original: string; label: string; index: number }) => (
                <Chip
                  key={`${index}-${original}`}
                  label={label}
                  title={label !== original ? original : undefined}
                  onClick={() => handlePrefillFromChip(original, index)}
                  sx={(t) => ({
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    height: 32,
                    px: 0.5,
                    maxWidth: 360,
                    bgcolor: t.tokens.surface.raised,
                    border: `1px solid ${t.tokens.border.default}`,
                    color: t.tokens.text.secondary,
                    cursor: 'pointer',
                    borderRadius: `${t.ds.radius.full}px`,
                    transition: `background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}, border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}, color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                    '&:hover': {
                      bgcolor: t.tokens.brand.primarySoft,
                      borderColor: t.tokens.brand.primary,
                      color: t.tokens.brand.primary,
                    },
                  })}
                />
              ))}
              {isPowerUser && !showAllSuggestions && suggestedPrompts.length > POWER_USER_CHIP_LIMIT && (
                <Button
                  size="small"
                  onClick={() => setShowAllSuggestions(true)}
                  sx={(t) => ({
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    height: 32,
                    px: 1,
                    color: t.tokens.text.tertiary,
                    '&:hover': {
                      bgcolor: 'transparent',
                      color: t.tokens.text.secondary,
                    },
                  })}
                >
                  Show more
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ─── Mobile sessions list (xs/sm only) ──────────────────────────── */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '0.9375rem',
              color: tokens.text.primary,
              letterSpacing: '-0.005em',
            }}
          >
            Sessions
            {threads.length > 0 && (
              <Box
                component="span"
                sx={{ ml: 0.75, fontWeight: 500, fontSize: '0.75rem', color: tokens.text.tertiary }}
              >
                {threads.length}
              </Box>
            )}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddRounded sx={{ fontSize: '14px !important' }} />}
            onClick={handleNewSession}
            sx={(t) => ({
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.75rem',
              px: 1.25,
              py: 0.5,
              height: 32,
              borderRadius: `${t.ds.radius.sm}px`,
              borderColor: t.tokens.border.default,
              color: t.tokens.text.secondary,
              flexShrink: 0,
              '&:hover': {
                borderColor: t.tokens.brand.primary,
                color: t.tokens.brand.primary,
                bgcolor: t.tokens.brand.primarySoft,
              },
            })}
          >
            New
          </Button>
        </Box>

        {/* Mobile search */}
        {threads.length > 0 && (
          <TextField
            size="small"
            fullWidth
            placeholder="Search sessions"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded sx={{ fontSize: 16, color: tokens.text.tertiary }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                    sx={{ p: 0.25 }}
                  >
                    <ClearRounded sx={{ fontSize: 14, color: tokens.text.tertiary }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
              sx: {
                fontSize: '0.8125rem',
                height: 32,
                borderRadius: `${theme.ds.radius.sm}px`,
                bgcolor: cardBg,
                '& fieldset': { borderColor: tokens.border.default },
              },
            }}
          />
        )}

        {/* Mobile list — same items, framed in a card */}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: `${theme.ds.radius.md}px` }} />
            ))}
          </Box>
        ) : threads.length === 0 ? (
          <Box
            sx={{
              py: 3,
              px: 3,
              textAlign: 'center',
              border: `1px dashed ${cardBorder}`,
              borderRadius: `${theme.ds.radius.lg}px`,
              bgcolor: cardBg,
            }}
          >
            <Typography sx={{ fontSize: '0.8125rem', color: tokens.text.tertiary }}>
              No sessions yet — ask your first question above.
            </Typography>
          </Box>
        ) : isSearching && filteredThreads.length === 0 ? (
          <Box
            sx={{
              py: 3,
              px: 3,
              textAlign: 'center',
              border: `1px dashed ${cardBorder}`,
              borderRadius: `${theme.ds.radius.lg}px`,
              bgcolor: cardBg,
            }}
          >
            <Typography sx={{ fontSize: '0.8125rem', color: tokens.text.tertiary }}>
              No sessions match &ldquo;{searchQuery}&rdquo;.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={(t) => ({
              border: `1px solid ${t.tokens.border.subtle}`,
              borderRadius: `${t.ds.radius.lg}px`,
              bgcolor: t.tokens.surface.raised,
              overflow: 'hidden',
              p: 0.5,
            })}
          >
            {filteredThreads.map((thread) => (
              <RailSessionItem
                key={thread.threadId}
                thread={thread}
                onOpen={handleOpenThread}
                onRequestDelete={handleRequestDelete}
              />
            ))}
          </Box>
        )}
      </Box>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => {
          if (!deleting) setPendingDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        tone="destructive"
        title="Delete this session?"
        confirmLabel="Delete"
        busy={deleting}
        description="The session will be hidden from your sessions list. You can recover it by contacting support if needed."
        body={
          pendingDelete && (
            <Box
              sx={(t) => ({
                p: 1.25,
                bgcolor: t.tokens.surface.sunken,
                borderRadius: `${t.ds.radius.sm}px`,
                border: `1px solid ${t.tokens.border.subtle}`,
              })}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {pendingDelete.title || 'Untitled session'}
              </Typography>
            </Box>
          )
        }
      />
    </Box>
  );
}
